/**
 * Run lifecycle: start, round pacing, defeat, victory, restart.
 *
 * Round pacing sits behind `RoundDriver`, which `Waves.ts`'s `WaveDirector`
 * satisfies structurally. `createRoundDriver()` is the one place the driver is
 * chosen; everything else in the game reads the interface.
 *
 * Nothing here imports Three.js. A run can be driven headless in a test.
 */

import type { Phase, World } from '@/core/types';
import { resetEnemies, setWaveHpScale } from './entities/Enemy';
import { resetEconomy } from './Economy';
import { resetWaves, waveDirector, waveHpScaleFor } from './Waves';
import type { RoundView } from './Waves';
import type { Ability, Act } from './Levels';
import { ACTS, TOTAL_ROUNDS, actOf, isActFinale, offersFor, towersUnlockedAt } from './Levels';

export type { RoundEntry, RoundView } from './Waves';

// ────────────────────────────────────────────────────────── round driver ──

/**
 * The seam between the run and its pacing. A driver owns `world.round`,
 * `world.pending` and the prep/combat halves of `world.phase`; it never decides
 * that a run is over, and it never touches lives.
 */
export interface RoundDriver {
  readonly name: string;
  reset(world: World): void;
  /** One sim step. Runs first, before spawning. */
  step(dt: number, world: World): void;
  /** Player pressed send. Returns the cash bonus awarded, or 0. */
  startEarly(world: World): number;
  view(world: World): RoundView;
  finished(world: World): boolean;
}

/**
 * The one place the wave manager is chosen.
 *
 * `WaveDirector` also owns the hit-point curves, so installing it means
 * installing `Waves.waveHpScaleFor` into the enemy pool at the same time — an
 * enemy spawned by a director whose scaling is not wired would carry round-1
 * health into round 80, and a boss handed the *trash* curve carries forty
 * million and is unkillable. See `Enemy.setWaveHpScale`.
 */
export function createRoundDriver(seed?: string): RoundDriver {
  setWaveHpScale(waveHpScaleFor);
  return waveDirector(seed);
}

// ───────────────────────────────────────────────────────────────── session ──

export interface RunStats {
  kills: number;
  cashEarned: number;
  livesLost: number;
  towersBuilt: number;
  /** Sim seconds. */
  duration: number;
}

export interface SessionHooks {
  /** Fired after `world.phase` changes, with the phase it left. */
  onPhase?(phase: Phase, previous: Phase): void;
  onRoundStart?(round: number): void;
  onRoundEnd?(round: number): void;
  onDefeat?(): void;
  onVictory?(): void;
  /** Clear renderers and pools. Fired inside `start`, before the world is seeded. */
  onReset?(): void;
  /**
   * An act has ended and the next one is waiting on the player. The session is
   * held here until {@link Session.chooseAbility} is called.
   *
   * `main.ts` should install the next act's map (`Track.installMap(world,
   * next.map)`), clear the board's projectiles and effects, and show the
   * intermission panel. Towers are *not* cleared by the session — see
   * {@link Session.beginAct}.
   */
  onActEnd?(finished: Act, next: Act): void;
  /** The next act has begun. Fired after the pick, with what the player took. */
  onActStart?(act: Act, taken: Ability | null): void;
}

/**
 * Why the clock is stopped.
 *
 * `player` is the pause menu and the pause key. `modal` is any surface that
 * covers the board without asking the player for a decision about it —
 * Settings is the only one today. `act` is the intermission, which owes the
 * player a pick and must not be resumed out of.
 *
 * A set rather than a boolean, because they overlap: opening Settings from
 * inside the pause menu adds a second reason, and closing it must drop only
 * that one. The clock starts again when the last reason goes.
 */
export type ClockHold = 'player' | 'modal' | 'act';

/**
 * The intermission between two acts.
 *
 * Held state rather than a phase transition the driver owns, because the driver
 * has no opinion about maps or abilities and should keep none.
 */
export interface Intermission {
  finished: Act;
  next: Act;
  /** The two abilities on offer. Exactly one is taken. */
  offers: readonly Ability[];
  /** Towers the next act unlocks, in catalogue order. */
  unlocks: readonly string[];
}

/**
 * Owns the run. It steps the round driver, watches lives and the round counter,
 * and fires defeat or victory exactly once.
 *
 * The driver sets `world.phase` to `defeat`/`victory` itself, so this class
 * reads the phase rather than second-guessing the rule. `RoundDriver.finished`
 * is deliberately not the victory test: a director can report "on the last
 * round" before that round has been survived.
 */
export class Session {
  readonly stats: RunStats = {
    kills: 0, cashEarned: 0, livesLost: 0, towersBuilt: 0, duration: 0,
  };

  seed = 'pixel-td';

  private lastPhase: Phase = 'boot';
  private lastRound = 0;
  private lastLives = 0;
  private lastCash = 0;
  private over = false;
  /** Phase held while paused, restored on resume. */
  private resumePhase: Phase = 'prep';
  /** Reasons the clock is stopped. Empty means it runs. See {@link ClockHold}. */
  private readonly holds = new Set<ClockHold>();

  /**
   * Simulation steps the board keeps running after the run is lost.
   *
   * See {@link holdDefeat}. Zero at every other moment of a run's life, so a
   * caller that never asks for a death sequence sees the behaviour this class
   * has always had.
   */
  private defeatTicks = 0;

  /** Ability ids the player has taken, one per act transition, in order. */
  readonly abilities: string[] = [];
  /** Non-null while an act transition is waiting on a pick. */
  private pending: Intermission | null = null;

  constructor(
    readonly world: World,
    readonly driver: RoundDriver,
    private readonly hooks: SessionHooks = {},
  ) {}

  get running(): boolean {
    const p = this.world.phase;
    return p === 'prep' || p === 'combat';
  }

  get paused(): boolean {
    return this.world.phase === 'paused';
  }

  get finished(): boolean {
    const p = this.world.phase;
    return p === 'defeat' || p === 'victory';
  }

  /**
   * True when the simulation must not advance. The one predicate the frame loop
   * reads, so every surface that stops the board stops it the same way.
   *
   * A finished run counts. The end-of-run sheet needs no hold of its own —
   * `defeat` and `victory` are terminal phases and there is nothing under that
   * sheet the player can still affect, so anything that kept stepping behind it
   * was burning frames to walk bodies past a board nobody owns any more.
   *
   * The one exception is bought explicitly. {@link holdDefeat} keeps the clock
   * running for a fixed count of steps past the loss so the death sequence has
   * frames to play in; the round driver is already stopped by then (see
   * {@link step}), so those frames move particles and nothing else.
   */
  get held(): boolean {
    return this.paused || (this.finished && this.defeatTicks <= 0);
  }

  /**
   * Buys `ticks` more simulation steps after defeat.
   *
   * Losing is the one moment the board has something left to say and no clock
   * to say it in: the driver flips the phase, {@link held} goes true on the
   * same frame, and the frame loop stops stepping before a single particle has
   * moved. So `main.ts` asks for the sequence's exact length here, from inside
   * `onDefeat`, and the sim runs on for precisely that many steps.
   *
   * Refused outside `defeat`. Victory earns no sequence, and a pause is not a
   * licence to run the board.
   */
  holdDefeat(ticks: number): void {
    if (this.world.phase !== 'defeat') return;
    this.defeatTicks = ticks > 0 ? Math.round(ticks) : 0;
  }

  /** Steps still owed to the death sequence. Zero at every other time. */
  get defeatHold(): number {
    return this.defeatTicks;
  }

  /** Reasons the clock is currently stopped, for the UI and for tests. */
  get holdReasons(): ReadonlySet<ClockHold> {
    return this.holds;
  }

  /** Wipes the board and starts round 1's prep window. */
  start(): void {
    const w = this.world;
    this.hooks.onReset?.();

    resetEnemies(w);
    w.towers.length = 0;
    w.projectiles.length = 0;
    w.pending.length = 0;
    w.fx.particles.length = 0;
    w.fx.shockwaves.length = 0;
    w.fx.numbers.length = 0;
    w.fx.shake = 0;
    w.time = 0;
    w.tick = 0;
    w.speed = 1;

    resetEconomy(w);
    resetWaves(w, this.seed);
    this.driver.reset(w);

    this.stats.kills = 0;
    this.stats.cashEarned = 0;
    this.stats.livesLost = 0;
    this.stats.towersBuilt = 0;
    this.stats.duration = 0;

    this.over = false;
    this.defeatTicks = 0;
    this.lastRound = 0;
    this.lastLives = w.lives;
    this.lastCash = w.cash;
    this.resumePhase = 'prep';
    this.abilities.length = 0;
    this.pending = null;
    // A restart is reached from behind a modal every time — the pause menu's
    // RESTART, the game-over sheet's TRY AGAIN — and the caller closes those
    // *after* this returns. A hold left standing would start the new run paused.
    this.holds.clear();

    this.setPhase(w.phase);
  }

  // ────────────────────────────────────────────────────────────── acts ──

  /**
   * The round the board is being built for.
   *
   * Not `world.round`, and the difference is a whole act wide. `WaveDirector`
   * only advances the counter when a wave *begins*, so throughout the prep
   * window that follows act I the counter still reads 12 — and that prep window
   * is act II's opening, fought on act II's map, with act II's shelf. Keying the
   * shelf off `world.round` would hand the player outpost's three towers on
   * vortex until the first body spawned.
   *
   * A pause reads through to whatever it interrupted, so pausing mid-combat does
   * not silently advance the shelf by a round.
   */
  get boardRound(): number {
    const w = this.world;
    const phase = w.phase === 'paused' ? this.resumePhase : w.phase;
    if (phase === 'combat') return Math.max(1, w.round);
    return Math.min(TOTAL_ROUNDS, w.round + 1);
  }

  /** The act the run is in. */
  get act(): Act {
    return actOf(this.boardRound);
  }

  /** The transition waiting on a pick, or null. */
  get intermission(): Intermission | null {
    return this.pending;
  }

  /** True while the run is held between acts. */
  get betweenActs(): boolean {
    return this.pending !== null;
  }

  /** Tower archetype ids the player may build right now. */
  unlockedTowers(): string[] {
    return towersUnlockedAt(this.boardRound);
  }

  /**
   * Cash the board would return if the act ended now. The intermission panel
   * shows this *before* the pick, so the player is told the corridor is about to
   * take their towers rather than finding out afterwards.
   */
  boardValue(): number {
    let sum = 0;
    for (const t of this.world.towers) sum += t.invested;
    return sum;
  }

  /** True if the player holds `id`. Cheap enough to call from a hot path. */
  hasAbility(id: string): boolean {
    return this.abilities.includes(id);
  }

  /**
   * Takes one of the two abilities on offer and starts the next act.
   *
   * Refuses an id that is not on offer, so a UI bug cannot hand the player the
   * whole tree. Returns the ability taken, or null if there was nothing to take.
   */
  chooseAbility(id: string): Ability | null {
    const held = this.pending;
    if (!held) return null;
    const offered = held.offers.find((a) => a.id === id);
    if (!offered) return null;
    this.abilities.push(offered.id);
    this.beginAct(held.next, offered);
    return offered;
  }

  /**
   * Starts the next act without taking anything. Exists so a UI dismiss cannot
   * wedge the run, not because refusing is a strategy.
   */
  skipAbility(): void {
    const held = this.pending;
    if (held) this.beginAct(held.next, null);
  }

  /**
   * Clears the board into the wallet and resumes on the next act.
   *
   * **Towers do not carry across an act.** The map changes, and a tower's
   * position is a point on a spline that no longer exists — remapping it would
   * be arbitrary, and leaving it standing would put emplacements inside the new
   * corridor. So the whole board is refunded at *full* price, not at
   * `ECONOMY.sellRatio`, and the player rebuilds.
   *
   * A full refund is also what `Balance.buildForBudget` already models: it buys
   * a fresh board out of `cashEntering(round)` on every round, and has since
   * before acts existed. Charging a sell penalty at each transition would make
   * the report optimistic by exactly seven times thirty percent of a board.
   */
  private beginAct(act: Act, taken: Ability | null): void {
    const w = this.world;
    for (const t of w.towers) w.cash += t.invested;
    w.towers.length = 0;
    w.projectiles.length = 0;
    this.lastCash = w.cash;
    this.pending = null;
    this.resumePhase = 'prep';
    this.holds.delete('act');
    // Only if nothing else is still holding — Settings opened over the panel is
    // the case, and resuming under it is the bug this whole seam exists to stop.
    if (this.holds.size === 0) this.setPhase('prep');
    this.hooks.onActStart?.(act, taken);
  }

  /**
   * Opens the intermission that follows `finishedRound` without fighting the
   * round first, and reports whether one opened.
   *
   * Capture harness and tests only. It exists so `dev/scenarios.ts` can
   * photograph a real transition rather than a mock of one: the panel it shows
   * is the panel the run builds, from the same code path, so a shot that looks
   * right is evidence the transition is right.
   */
  openIntermission(finishedRound: number): boolean {
    this.checkActBoundary(finishedRound);
    return this.pending !== null;
  }

  /**
   * Called when a round finishes. Opens the intermission if that round was an
   * act finale and there is another act after it.
   */
  private checkActBoundary(finishedRound: number): void {
    if (finishedRound <= 0 || !isActFinale(finishedRound)) return;
    const finished = actOf(finishedRound);
    const next = ACTS[finished.index];
    if (!next) return;
    this.pending = {
      finished,
      next,
      offers: offersFor(next),
      unlocks: next.unlocks.slice(),
    };
    // The intermission is a decision point with no clock on it, so it takes the
    // same hold every other modal takes rather than posing the phase by hand.
    this.setHold('act', true);
    this.resumePhase = 'prep';
    this.hooks.onActEnd?.(finished, next);
  }

  restart(): void {
    this.start();
  }

  /**
   * One simulation step's worth of lifecycle. Call at the top of the step, so a
   * round that starts this step also spawns this step.
   */
  step(dt: number): void {
    const w = this.world;
    // Counted down before the guard below, and it has to be: the run is already
    // `over` by the time the death sequence is playing, so a decrement placed
    // after the early return would never happen and the hold would never end.
    if (this.defeatTicks > 0) this.defeatTicks--;
    if (this.over || !this.running) return;

    this.stats.duration += dt;
    const phaseBefore = w.phase;
    this.driver.step(dt, w);
    // A round is over the instant the driver hands the phase back to prep. That
    // edge is the only place an act can end, and it has to be read here rather
    // than from `world.round` because the round counter does not move until the
    // *next* round begins.
    const roundEnded = phaseBefore === 'combat' && w.phase === 'prep';

    if (w.round !== this.lastRound) {
      if (this.lastRound > 0) this.hooks.onRoundEnd?.(this.lastRound);
      this.lastRound = w.round;
      this.hooks.onRoundStart?.(w.round);
    }

    if (w.lives < this.lastLives) {
      this.stats.livesLost += this.lastLives - w.lives;
      this.lastLives = w.lives;
    }
    if (w.cash > this.lastCash) this.stats.cashEarned += w.cash - this.lastCash;
    this.lastCash = w.cash;

    // The driver already flipped the phase; this only reacts to it once.
    if (w.phase === 'defeat') {
      this.over = true;
      this.setPhase('defeat');
      this.hooks.onDefeat?.();
      return;
    }
    if (w.phase === 'victory') {
      this.over = true;
      this.setPhase('victory');
      this.hooks.onVictory?.();
      return;
    }

    if (roundEnded) this.checkActBoundary(w.round);
  }

  /** Counted from `main.ts`'s kill hook, the only place a kill is observable. */
  noteKill(): void {
    this.stats.kills++;
  }

  noteTowerBuilt(): void {
    this.stats.towersBuilt++;
  }

  startEarly(): number {
    if (!this.running) return 0;
    return this.driver.startEarly(this.world);
  }

  view(): RoundView {
    return this.driver.view(this.world);
  }

  /**
   * Adds or drops one reason to stop the clock.
   *
   * The phase is the mechanism, exactly as the pause key has always used it:
   * the first hold saves whichever half of the round it interrupted and moves
   * the world into `paused`, and the last one to leave puts it back. Nothing
   * else in the game stops the sim, so there is one thing to reason about
   * rather than one per modal.
   *
   * A run that is not running cannot be held. `defeat` and `victory` are
   * already terminal — overwriting them with `paused` would lose the result the
   * game-over sheet is reading — and `boot` has no round to interrupt. Both
   * halves are guarded, so `setHold(x, false)` after a refused open is a no-op
   * rather than a resume into a phase that never happened.
   */
  setHold(reason: ClockHold, on: boolean): void {
    const w = this.world;
    if (on) {
      if (this.holds.has(reason)) return;
      if (this.holds.size === 0) {
        if (!this.running) return;
        this.resumePhase = w.phase;
        this.setPhase('paused');
      }
      this.holds.add(reason);
      return;
    }
    if (!this.holds.delete(reason)) return;
    if (this.holds.size === 0 && w.phase === 'paused') this.setPhase(this.resumePhase);
  }

  setPaused(paused: boolean): void {
    // An intermission holds the run in `paused` while it owes the player an
    // ability. Letting the pause key out of it would resume a run standing on
    // the old act's map with the panel still up and the pick still unmade.
    if (this.pending !== null) return;
    this.setHold('player', paused);
  }

  /**
   * Flips the player's own hold, not the world's phase.
   *
   * Reading the phase would make this mean "resume" while a menu is holding the
   * clock for its own reasons, and the player never asked for that.
   */
  togglePause(): void {
    this.setPaused(!this.holds.has('player'));
  }

  private setPhase(phase: Phase): void {
    const previous = this.lastPhase;
    this.world.phase = phase;
    this.lastPhase = phase;
    if (phase !== previous) this.hooks.onPhase?.(phase, previous);
  }
}

/** The pacing in play is the real `Waves.ts` director, not a stand-in. */
export const usingStubWaves = false;
